import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { History, Loader2, Download, RefreshCcw, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import { format } from 'date-fns';

const ReadinessHistoryCard = ({
  history, historyLoading, exportingCsv,
  exportHistoryCsv, loadHistory,
}) => (
  <Card className="border-[#E5E7EB]" data-testid="readiness-history-card">
    <CardHeader className="pb-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <History className="w-4 h-4 text-[#002FA7]" />Readiness Check History
          <span className="text-[10px] font-normal text-[#9CA3AF]">(last {Math.min(history.length, 20)} runs)</span>
        </CardTitle>
        <div className="flex items-center gap-1">
          <Button
            size="sm" variant="outline" onClick={exportHistoryCsv}
            disabled={exportingCsv || history.length === 0}
            data-testid="export-history-csv-btn"
          >
            {exportingCsv ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Download className="w-3 h-3 mr-1" />}
            Export CSV
          </Button>
          <Button size="sm" variant="ghost" onClick={loadHistory} disabled={historyLoading} data-testid="refresh-history-btn">
            <RefreshCcw className={`w-3 h-3 mr-1 ${historyLoading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </div>
    </CardHeader>
    <CardContent>
      {history.length === 0 ? (
        <p className="text-[11px] text-[#9CA3AF] italic" data-testid="history-empty">
          No runs yet — run a Readiness Check above to start building audit-ready history.
        </p>
      ) : (
        <>
          {/* Pass-rate sparkline */}
          <div className="mb-3 p-3 border border-[#E5E7EB] rounded-lg" data-testid="history-sparkline">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Pass rate trend</p>
              <p className="text-[10px] text-[#4B5563]">
                Avg&nbsp;
                <strong className="text-[#0A0A0A]">
                  {Math.round(
                    (history.reduce((s, r) => s + (r.total ? r.passed / r.total : 0), 0) / history.length) * 100
                  )}%
                </strong>
              </p>
            </div>
            <ResponsiveContainer width="100%" height={80}>
              <LineChart
                data={[...history].slice(0, 10).reverse().map((r, i) => ({
                  idx: i + 1,
                  rate: r.total ? Math.round((r.passed / r.total) * 100) : 0,
                  passed: r.passed,
                  total: r.total,
                  run_at: r.run_at,
                }))}
                margin={{ top: 6, right: 8, left: -24, bottom: 0 }}
              >
                <XAxis dataKey="idx" hide />
                <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: '#9CA3AF' }} tickLine={false} axisLine={false} width={30} />
                <RTooltip
                  contentStyle={{ fontSize: 11, padding: 6 }}
                  labelFormatter={() => ''}
                  formatter={(v, _n, p) => [
                    `${p.payload.passed}/${p.payload.total} (${v}%)`,
                    format(new Date(p.payload.run_at), 'MMM d, HH:mm'),
                  ]}
                />
                <Line type="monotone" dataKey="rate" stroke="#002FA7" strokeWidth={2} dot={{ r: 2, fill: '#002FA7' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Table of recent runs */}
          <div className="space-y-1" data-testid="history-list">
            {history.slice(0, 10).map((r, i) => {
              const rate = r.total ? Math.round((r.passed / r.total) * 100) : 0;
              return (
                <div
                  key={r.run_at + i}
                  className="flex items-center gap-2 p-2 rounded border border-[#E5E7EB] hover:bg-[#FAFAFA]"
                  data-testid={`history-row-${i}`}
                >
                  {r.all_ok ? (
                    <CheckCircle2 className="w-4 h-4 text-[#34C759] flex-shrink-0" />
                  ) : r.passed > 0 ? (
                    <AlertTriangle className="w-4 h-4 text-[#FF9500] flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-[#FF3B30] flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0 flex items-center gap-3 flex-wrap">
                    <span className="text-xs font-medium text-[#0A0A0A]">
                      {format(new Date(r.run_at), 'MMM d, yyyy HH:mm')}
                    </span>
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${r.all_ok ? 'text-[#34C759] border-[#34C759]/40' : r.passed > 0 ? 'text-[#FF9500] border-[#FF9500]/40' : 'text-[#FF3B30] border-[#FF3B30]/40'}`}
                    >
                      {r.passed}/{r.total} passed · {rate}%
                    </Badge>
                    <span className="text-[10px] text-[#4B5563] truncate">
                      by <strong>{r.triggered_by || '—'}</strong> → <code>{r.recipient}</code>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </CardContent>
  </Card>
);

export default ReadinessHistoryCard;
